
import { setzeFarbhinweis } from '@theme/dp-ohne-bild';
import { bildSichtbar, auswahlAusDom } from '@theme/dp-alt-marken';
import { setzeVariantenVerweise } from '@theme/dp-variantenverweis';

/**
 * Schreibt Fokus und Zoom an ein Kachelbild - als Variablen und Klasse fuer
 * das Stilblatt (.dp-zoomt vergroessert ohne transform um den Fokus).
 *
 * @param {HTMLImageElement} bild
 * @param {string} fokus - "x% y%" oder leer
 * @param {string} zoom - Faktor oder leer
 */
function setzeFokusUndZoom(bild, fokus, zoom) {
  const ziel = fokus || '';
  if (bild.style.objectPosition !== ziel) bild.style.objectPosition = ziel;
  const teile = ziel.split(' ');
  bild.style.setProperty('--dp-fokus-x', teile[0] || '');
  bild.style.setProperty('--dp-fokus-y', teile[1] || '');
  bild.style.setProperty('--dp-zoom', zoom || '');
  bild.classList.toggle('dp-zoomt', !!zoom);
}

/**
 * Verbindet die Optionsauswahl im Schnellkauf-Feld mit Horizons Produktformular.
 *
 * Horizons eigener Variant-Picker ist fuer die Produktseite gebaut und laesst
 * die Section ueber die Rendering-API neu zeichnen. In einem Feld, das nur
 * beim Hover aufgeht, waere das zu schwer. Hier reicht eine kleine Zuordnung:
 * die gewaehlten Optionswerte werden auf eine Variante abgebildet, deren ID in
 * das versteckte Feld des Formulars geschrieben wird. Alles Weitere - Warenkorb
 * per Fetch, Cart-Drawer, Fehlerbehandlung - uebernimmt product-form-component.
 *
 * Die Optionsfelder stehen bewusst ausserhalb des <form>, damit sie nicht als
 * unbekannte Felder im Warenkorb-Request landen.
 */

/*
 * Ueberblendung der Vorschaubilder beim Farbwechsel.
 *
 * Frueher tauschte ein Klick auf einen Farbpunkt die Bilder schlagartig aus.
 * Jetzt legt sich im Moment des Tauschs ein Abzug der bisherigen Bilder
 * deckungsgleich ueber das Raster (.dp-product__panel-blende), darunter
 * stehen schon die Bilder der neuen Farbe, und der Abzug blendet aus - eine
 * Ueberblendung ohne Luecke, weil unter dem ausblendenden Bild immer ein
 * volles Bild liegt.
 *
 * Getauscht wird erst, wenn die neuen Bilder geladen und entschluesselt
 * sind: sonst blendete der Abzug ueber leeren Faechern aus, und das Feld
 * blitzte hell auf. Laedt ein Bild zaeh, wird nach VORLADE_FRIST trotzdem
 * getauscht - lieber ein spaet einlaufendes Bild als ein Klick ohne Wirkung.
 */

/** So lange wird hoechstens auf die Bilder der neuen Farbe gewartet. */
const VORLADE_FRIST = 2500;

/** Rueckfall fuer die Dauer der Blende, falls das Stilblatt keine nennt. */
const BLENDE_RUECKFALL = 320;

/**
 * Laedt und entschluesselt ein Bild vorab. Erfuellt sich immer - auch bei
 * einem Fehler, denn dann gibt es eben nichts mehr abzuwarten.
 *
 * @param {string} quelle
 * @returns {Promise<void>}
 */
function ladeVor(quelle) {
  return new Promise((fertig) => {
    const bild = new Image();
    bild.decoding = 'async';
    bild.src = quelle;
    const decode = typeof bild.decode === 'function' ? bild.decode() : Promise.resolve();
    decode.then(
      () => fertig(),
      () => fertig()
    );
  });
}

/**
 * Wartet auf alle Versprechen, aber hoechstens `frist` Millisekunden.
 *
 * @param {Promise<unknown>[]} versprechen
 * @param {number} frist
 * @returns {Promise<void>}
 */
function hoechstens(versprechen, frist) {
  return Promise.race([
    Promise.all(versprechen).then(() => undefined),
    new Promise((fertig) => window.setTimeout(fertig, frist)),
  ]);
}

/** Wuenscht der Besucher reduzierte Bewegung? */
function ohneBewegung() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/** Beschriftung des Stifts: "Erst eine Größe wählen", "Erst eine Farbe wählen". */
function erstWaehlen(name) {
  const klein = String(name || '').toLowerCase();
  if (/(farbe|colou?r)/.test(klein)) return 'Erst eine Farbe wählen';
  if (/(größe|groesse|grösse|size)/.test(klein)) return 'Erst eine Größe wählen';
  return 'Erst ' + (String(name || '').trim() || 'eine Auswahl') + ' wählen';
}

class DpQuickBuy extends HTMLElement {
  connectedCallback() {
    const data = this.querySelector('[data-dp-variants]');
    if (!data) return;

    try {
      this.variants = JSON.parse(data.textContent);
    } catch {
      this.variants = [];
    }

    this.soldOutLabel = this.dataset.soldOutLabel || 'Ausverkauft';
    this.addLabel = this.knopf
      ? this.knopf.getAttribute('aria-label') || this.knopf.textContent.trim()
      : '';

    this.handleChange = this.handleChange.bind(this);
    this.addEventListener('change', this.handleChange);

    /*
     * Ein Klick auf ein Options-Etikett darf den Fokus nicht verschieben.
     *
     * Etiketten sind nicht fokussierbar: der Fokus des zuvor gewaehlten
     * Radios ginge beim Niederdruecken ins Leere (blur ohne relatedTarget),
     * und Horizons Mega-Menue wertet genau das als "Fokus hat das Menue
     * verlassen" - es schloss sich beim zweiten Farbklick. Ohne die
     * Fokusuebergabe bleibt das Menue stehen; das Anhaken der Option
     * geschieht davon unberuehrt erst beim Klick.
     */
    this.addEventListener('mousedown', (ereignis) => {
      // Nur die Farb-Etiketten - die Groessenauswahl steckt ebenfalls in
      // einem Etikett, und ihr Aufklappen lebt vom Niederdruecken.
      if (ereignis.target instanceof Element && ereignis.target.closest('.dp-product__option--farben label')) {
        ereignis.preventDefault();
      }
    });

    /*
     * Fehlt die Farbe, zeigt der Kaufknopf den Stift - wie im Kaufoverlay der
     * Produktseite (dp-kaufknopf--ohne-farbe). Er bleibt bedienbar, legt aber
     * nichts in den Warenkorb: sein Klick mahnt die Farbwahl an. Abgefangen
     * wird in der Einfangphase, bevor das Formular des Themes ihn sieht -
     * ebenso das Absenden mit der Eingabetaste aus dem Mengenfeld.
     */
    const halteOhneFarbe = (ereignis) => {
      const knopf = this.knopf;
      if (!knopf || !knopf.classList.contains('dp-kaufknopf--ohne-farbe')) return;
      if (ereignis.type === 'click' && !(ereignis.target instanceof Element && ereignis.target.closest('button[name="add"]'))) return;
      ereignis.preventDefault();
      ereignis.stopImmediatePropagation();
      this.mahneFarbe();
    };
    this.addEventListener('click', halteOhneFarbe, true);
    this.addEventListener('submit', halteOhneFarbe, true);

    this.handleChange();
  }

  /**
   * Die Optionen, die noch eine Wahl verlangen: die Farbpunkte ohne Haken und
   * jede Auswahlliste, in der noch der Platzhalter steht (Groesse, Material
   * ...). Name fuer die Beschriftung des Knopfs gleich mit.
   *
   * @returns {{ feld: Element, name: string }[]}
   */
  offeneOptionen() {
    const offen = [];
    const farbfeld = this.querySelector('.dp-product__option--farben');
    if (farbfeld && !farbfeld.querySelector('input[type="radio"]:checked')) {
      offen.push({ feld: farbfeld, name: farbfeld.querySelector('legend')?.textContent?.trim() || 'Farbe' });
    }
    for (const liste of this.querySelectorAll('select[data-dp-pflichtwahl]')) {
      if (!(liste instanceof HTMLSelectElement) || liste.value !== '') continue;
      const feld = liste.closest('.dp-product__option') || liste;
      offen.push({ feld, name: liste.options[0]?.textContent?.trim() || '' });
    }
    // In der Reihenfolge, in der sie im Feld stehen - der Knopf nennt die erste.
    return offen.sort((a, b) =>
      a.feld.compareDocumentPosition(b.feld) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1
    );
  }

  /** Hinterlegt jede offene Wahl rot, bis sie getroffen ist - wie im Kaufoverlay. */
  mahneFarbe() {
    for (const { feld } of this.offeneOptionen()) feld.classList.add('dp-farbe--fehlt');
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.handleChange);
  }

  /*
   * Bewusst jedes Mal neu gesucht statt einmal gemerkt - siehe oben.
   */

  /** @returns {HTMLInputElement | null} */
  get eingabe() {
    return this.querySelector('input[name="id"]');
  }

  /** @returns {HTMLElement | null} */
  get preisfeld() {
    return this.querySelector('[data-dp-price]');
  }

  /** @returns {HTMLButtonElement | null} */
  get knopf() {
    return this.querySelector('button[name="add"]');
  }

  /** @returns {string[]} Die aktuell gewaehlten Werte, nach Optionsposition. */
  selectedValues() {
    const werte = [];
    for (const input of this.querySelectorAll('input[type="radio"][data-option-index]:checked')) {
      werte[Number(input.dataset.optionIndex)] = input.value;
    }

    // Optionen ohne Farbfelder stehen als Auswahlliste im Feld. Der Platzhalter (leer) ist keine Wahl.
    for (const liste of this.querySelectorAll('select[data-option-index]')) {
      if (liste.value !== '') werte[Number(liste.dataset.optionIndex)] = liste.value;
    }
    return werte;
  }

  /*
   * Die Vorschaubilder des Feldes folgen der Auswahl: gezeigt werden die
   * Bilder, deren Alt-Marken ("farbe:...", "groesse:...") zur Auswahl passen,
   * plus die ohne Marke. Ohne Wahl stehen nur die Bilder ohne Marke -
   * dieselbe Regel wie in der Galerie der Produktseite (dp-bildfilter,
   * bildSichtbar in dp-alt-marken.js). Traegt ein Artikel
   * nur markierte Bilder, bleibt das Raster bis zur Farbwahl leer.
   */
  aktualisiereBilder() {
    const daten = this.querySelector('[data-dp-panel-bilder]');
    const medien = this.querySelector('.dp-product__panel-media');
    if (!daten || !medien) return;

    /*
     * Die Liste wird neu gelesen, sobald sich ihr Inhalt geaendert hat - nicht
     * nur beim ersten Mal. Beim Nachladen des Menues (Section Rendering,
     * morph) bleibt das Element bestehen und bekommt nur neuen Inhalt; ein
     * Zwischenspeicher vom ersten Mal zeigte dann die Bilder eines anderen
     * Artikels, waehrend Name und Preis schon die richtigen waren.
     */
    if (!this.panelBilder || this.panelBilderQuelle !== daten.textContent) {
      this.panelBilderQuelle = daten.textContent;
      try {
        this.panelBilder = JSON.parse(daten.textContent);
      } catch {
        this.panelBilder = [];
      }
      // Neuer Inhalt heisst neuer Aufbau: der erste Stand kommt ohne Blende.
      this.bilderStand = undefined;
    }
    if (!this.panelBilder.length) return;

    // Die Auswahl aller Optionen des Feldes (Schluessel an den Eingaben: data-dp-schluessel).
    const auswahl = auswahlAusDom(this.querySelector('.dp-product__options') || this);
    const optionen = (medien.dataset.dpOptionen || '').split('|').filter(Boolean);

    // Der Hinweis im leeren Raster: erst waehlen - oder die Wahl hat keine Bilder.
    setzeFarbhinweis(medien.querySelector(':scope > .dp-ohne-bild--feld'), auswahl);

    // Die allgemeinen Bilder ohne wirksame Marke - sie gelten fuer jede Wahl.
    const allgemeine = this.panelBilder.filter((bild) => bildSichtbar(bild.a, {}, optionen));
    const auswahlLeer = Object.keys(auswahl).length === 0;

    /*
     * Mit Farbe: ihre Bilder zuerst, dahinter die allgemeinen - sonst
     * fuellten die allgemeinen (sie stehen im Bestand oft vorn) alle Faecher,
     * und von der gewaehlten Farbe waere nichts zu sehen. Ohne Farbe nur die
     * allgemeinen. Bleibt nichts uebrig, steht das Raster leer.
     */
    const markierte = auswahlLeer
      ? []
      : this.panelBilder.filter((bild) => !bildSichtbar(bild.a, {}, optionen) && bildSichtbar(bild.a, auswahl, optionen));
    const liste = markierte.concat(allgemeine);

    const ziel = liste.slice(0, 4);

    /*
     * Der Stand, den die Faecher zeigen sollen - Adresse, Fokus und Zoom je
     * Fach. Der juengste Wunsch gewinnt: laufen mehrere Klicks schnell
     * hintereinander ein, wird nur der letzte getauscht, die dazwischen
     * verfallen, sobald ihre Bilder da sind.
     */
    const stand = ziel.map((bild) => bild.q + '|' + (bild.f || '') + '|' + (bild.z || '')).join('\n');
    this.bilderWunsch = stand;
    if (stand === this.bilderStand) return;

    /*
     * Ohne Blende: beim Aufbau (der erste Stand steht so schon im Markup),
     * bei reduzierter Bewegung und wenn das Raster gar nicht zu sehen ist -
     * in ganz schmalen Karten weicht es (display: none).
     */
    if (this.bilderStand === undefined || ohneBewegung() || !medien.getClientRects().length) {
      this.setzeBilder(medien, ziel, stand);
      return;
    }

    hoechstens(ziel.map((bild) => ladeVor(bild.q)), VORLADE_FRIST).then(() => {
      // Ueberholt von einem spaeteren Klick - dessen Bilder kommen dran.
      if (this.bilderWunsch !== stand || this.bilderStand === stand) return;
      if (!medien.isConnected) return;
      this.blendeUeber(medien, () => this.setzeBilder(medien, ziel, stand));
    });
  }

  /**
   * Schreibt die Bilder in die Faecher und stellt das Raster auf ihre Anzahl.
   *
   * @param {HTMLElement} medien
   * @param {{ q: string, f?: string, z?: string }[]} ziel
   * @param {string} stand
   */
  setzeBilder(medien, ziel, stand) {
    this.bilderStand = stand;

    // Nur die eigenen Faecher - nicht die im Abzug einer laufenden Blende.
    const plaetze = medien.querySelectorAll(':scope > .dp-product__panel-slot');
    plaetze.forEach((platz, stelle) => {
      const bild = platz.querySelector('img');
      if (stelle < ziel.length) {
        platz.hidden = false;
        if (bild && bild.getAttribute('src') !== ziel[stelle].q) {
          // srcset muss weichen, sonst waehlt der Browser weiter aus der alten Liste.
          bild.removeAttribute('srcset');
          bild.src = ziel[stelle].q;
        }
        // Der Fokuspunkt des Bildes wandert mit - leer heisst Mitte.
        // Fokus und Zoom des Bildes (Alt-Marke "zoom:XX") wandern mit - leer heisst Mitte und kein Zoom.
        if (bild) setzeFokusUndZoom(bild, ziel[stelle].f || '', ziel[stelle].z || '');
      } else {
        platz.hidden = true;
      }
    });

    // Das Raster rechnet mit der Anzahl - dieselben Regeln wie beim Aufbau:
    // ein Bild allein, zwei uebereinander, ab drei das Viererfeld. Null
    // Bilder: das leere Raster haelt seine Flaeche (Stilblatt, Anzahl 0).
    const anzahl = ziel.length;
    medien.dataset.dpAnzahl = String(anzahl);
    medien.style.setProperty('--dp-panel-cols', anzahl <= 2 ? '1' : '2');
    medien.style.setProperty('--dp-panel-rows', anzahl <= 1 ? '1' : '2');
  }

  /**
   * Legt einen Abzug des jetzigen Rasters darueber, fuehrt den Tausch
   * darunter aus und blendet den Abzug aus.
   *
   * Der Abzug ist eine Kopie des Rasters selbst, eingehaengt in das Raster
   * und mit inset: 0 auf dessen Flaeche gelegt. Er hat damit dieselbe Breite,
   * dieselbe Hoehe und dieselben Regeln wie das Original und legt sich Fach
   * fuer Fach deckungsgleich darueber - auch wenn darunter aus vier Bildern
   * zwei werden. Die Flaeche selbst bleibt stehen; nur ihr Inhalt blendet.
   *
   * Kommt ein neuer Wechsel, waehrend ein Abzug noch ausblendet, legt sich
   * der neue Abzug UNTER den alten: Er zeigt genau den Stand, der dort gerade
   * durchscheint. Das Bild bleibt also im Moment des Klicks unveraendert, und
   * jede Blende laeuft ungestoert zu Ende - nichts springt, nichts flackert.
   *
   * @param {HTMLElement} medien
   * @param {() => void} tausche
   */
  blendeUeber(medien, tausche) {
    /*
     * Aus dem leeren Raster heraus gibt es nichts abzuziehen: der Abzug waere
     * nur die zarte Flaeche des leeren Fachs, und durch sie hindurch staenden
     * die neuen Bilder sofort da. Dann blenden stattdessen die neuen Bilder
     * selbst auf - aus der leeren Flaeche heraus.
     */
    if (medien.dataset.dpAnzahl === '0' || medien.classList.contains('dp-product__panel-media--ein')) {
      this.blendeEin(medien, tausche);
      return;
    }

    const abzug = /** @type {HTMLElement} */ (medien.cloneNode(true));

    // Abzuege frueherer Blenden gehoeren nicht in den neuen.
    for (const alt of abzug.querySelectorAll(':scope > .dp-product__panel-blende')) alt.remove();

    abzug.classList.add('dp-product__panel-blende');
    abzug.setAttribute('aria-hidden', 'true');
    abzug.inert = true;

    /*
     * Laeuft gerade noch das Aufblenden aus dem leeren Raster (blendeEin),
     * haelt der Abzug genau den Zwischenstand fest - Deckkraft der Faecher
     * und Flaeche des Rasters -, sonst stuenden die Bilder im Abzug schlagartig
     * voll da.
     */
    abzug.style.backgroundColor = getComputedStyle(medien).backgroundColor;
    const eigeneFaecher = medien.querySelectorAll(':scope > .dp-product__panel-slot');
    abzug.querySelectorAll(':scope > .dp-product__panel-slot').forEach((fach, stelle) => {
      const original = eigeneFaecher[stelle];
      if (!(fach instanceof HTMLElement) || !original) return;
      const deckung = getComputedStyle(original).opacity;
      if (deckung !== '1') fach.style.opacity = deckung;
    });

    /*
     * Die Bilder des Abzugs zeigen genau die Fassung, die im Original gerade
     * zu sehen ist (currentSrc) - ohne srcset, damit der Browser nicht neu
     * waehlt, und sofort entschluesselt: Safari zeichnete ein frisch
     * angelegtes Bild sonst erst ein, zwei Bilder spaeter, und das Feld
     * blitzte kurz leer auf (siehe dp-galeriewechsel.js).
     */
    const originale = medien.querySelectorAll(':scope > .dp-product__panel-slot img');
    abzug.querySelectorAll(':scope > .dp-product__panel-slot img').forEach((kopie, stelle) => {
      if (!(kopie instanceof HTMLImageElement)) return;
      const original = originale[stelle];
      const quelle = original instanceof HTMLImageElement ? original.currentSrc || original.src : '';
      kopie.removeAttribute('srcset');
      kopie.removeAttribute('sizes');
      kopie.loading = 'eager';
      kopie.decoding = 'sync';
      if (quelle) kopie.src = quelle;
    });

    // Unter einen noch laufenden Abzug, sonst obenauf.
    medien.insertBefore(abzug, medien.querySelector(':scope > .dp-product__panel-blende'));

    tausche();

    let fertig = false;
    const wegraeumen = () => {
      if (fertig) return;
      fertig = true;
      abzug.remove();
    };

    const neue = [...medien.querySelectorAll(':scope > .dp-product__panel-slot:not([hidden]) img')];
    const entschluesselt = neue.map((bild) =>
      bild instanceof HTMLImageElement && typeof bild.decode === 'function' ? bild.decode().catch(() => {}) : null
    );

    // Die Bilder sind vorgeladen; das Warten hier ist nur das Netz darunter.
    hoechstens(entschluesselt.filter(Boolean), 400).then(() => {
      if (!abzug.isConnected) return;
      const dauer = parseFloat(getComputedStyle(abzug).transitionDuration) * 1000;
      const frist = Number.isFinite(dauer) ? dauer : BLENDE_RUECKFALL;

      abzug.addEventListener(
        'transitionend',
        (ereignis) => {
          if (ereignis.target === abzug) wegraeumen();
        }
      );
      // Kommt kein transitionend (Tab im Hintergrund, Dauer null), raeumt die Frist ab.
      window.setTimeout(wegraeumen, frist + 120);

      void abzug.offsetWidth;
      abzug.classList.add('dp-product__panel-blende--weg');
    });
  }

  /**
   * Laesst die Bilder aus dem leeren Raster aufblenden: Die Faecher stehen
   * unsichtbar bereit (dp-product__panel-media--ein), werden befuellt und
   * blenden auf, sobald ihre Bilder entschluesselt sind. Die Flaeche des
   * leeren Fachs haelt die Klasse so lange fest und gibt sie im selben Zug
   * ab.
   *
   * @param {HTMLElement} medien
   * @param {() => void} tausche
   */
  blendeEin(medien, tausche) {
    medien.classList.add('dp-product__panel-media--ein');
    tausche();

    const neue = [...medien.querySelectorAll(':scope > .dp-product__panel-slot:not([hidden]) img')];
    const entschluesselt = neue.map((bild) =>
      bild instanceof HTMLImageElement && typeof bild.decode === 'function' ? bild.decode().catch(() => {}) : null
    );

    hoechstens(entschluesselt.filter(Boolean), 400).then(() => {
      void medien.offsetWidth;
      medien.classList.remove('dp-product__panel-media--ein');
    });
  }

  handleChange() {
    if (!this.variants || !this.variants.length) return;

    this.aktualisiereBilder();

    /*
     * Solange keine Farbe gewaehlt ist (das Feld startet ohne Vorauswahl,
     * wie die Produktseite), bleibt der Kauf gesperrt - sonst landete
     * unbemerkt die Standardfarbe im Warenkorb. Preis und Vorschau zeigen
     * derweil den gerenderten Stand.
     */
    /*
     * Dasselbe gilt fuer jede andere Option mit mehr als einem Wert (Jimmi,
     * Oktober 2026): Groesse, Material - alles startet ohne Vorauswahl.
     */
    const offen = this.offeneOptionen();
    const knopfJetzt = this.knopf;

    // Was gewaehlt ist, ist nicht mehr anzumahnen.
    for (const feld of this.querySelectorAll('.dp-farbe--fehlt')) {
      if (!offen.some((eintrag) => eintrag.feld === feld)) feld.classList.remove('dp-farbe--fehlt');
    }

    const gewaehlt = this.selectedValues();
    const passend = this.variants.filter((variante) =>
      variante.options.every((wert, i) => gewaehlt[i] === undefined || gewaehlt[i] === wert)
    );

    // Die Verweise nehmen mit, was schon gewaehlt ist - auch eine Teilwahl.
    this.aktualisiereVerweise(this.varianteFuerSeite(passend), gewaehlt);

    if (offen.length) {
      /*
       * Zu haben ist der Artikel - es fehlt nur die Wahl. Der Knopf zeigt
       * deshalb den Stift statt des grauen Wagens und bleibt bedienbar; sein
       * Klick mahnt die offenen Optionen an (halteOhneFarbe).
       */
      if (knopfJetzt) {
        knopfJetzt.disabled = false;
        knopfJetzt.classList.add('dp-kaufknopf--ohne-farbe');
        knopfJetzt.setAttribute('aria-label', erstWaehlen(offen[0].name));
      }
      this.dataset.dpVerfuegbar = 'ja';
      return;
    }
    // Mit der letzten Wahl ist die Mahnung erledigt.
    if (knopfJetzt) knopfJetzt.classList.remove('dp-kaufknopf--ohne-farbe');
    /*
     * Der Kaufknopf nimmt den ersten Treffer, die Anzeige den guenstigsten.
     *
     * Artikel mit gerechnetem Betrag tragen ihre Preisstufen als Option
     * "Preis". Die wird im Feld nicht gezeigt und damit auch nicht gewaehlt -
     * es bleiben also mehrere Treffer uebrig, und der erste davon ist eine
     * beliebige Stufe. Genau daher kam der falsche Betrag in der Kachel.
     * Gezeigt wird deshalb der kleinste; davor steht ohnehin "ab".
     */
    const treffer = passend[0];
    let zeigen =
      passend.length > 1
        ? passend.reduce((klein, v) => (Number(v.cent) < Number(klein.cent) ? v : klein), passend[0])
        : treffer;

    if (!treffer) {
      this.markiereNichtVerfuegbar();
      return;
    }

    const eingabe = this.eingabe;
    if (eingabe) eingabe.value = treffer.id;

    const preisfeld = this.preisfeld;
    if (preisfeld) {
      /*
       * Die Regel des Artikels (snippets/dp-artikelpreis.liquid, unsichtbar
       * am Preis): Mit gewaehlter Farbe gilt der Preis dieser Variante plus
       * Pflichtaufpreis, ein hoeherer Mindestpreis sticht. "ab" bleibt nur,
       * wenn es auch ohne die Varianten mehr werden kann - beim ersten
       * Rechnen gemerkt, weil das Feld gleich neu geschrieben wird.
       */
      const regelEl = preisfeld.querySelector('[data-dp-preisregel]');
      if (regelEl && !this.preisRegel) {
        this.preisRegel = {
          pflicht: Number(regelEl.dataset.pflicht) || 0,
          mindest: Number(regelEl.dataset.mindest) || 0,
          abRest: regelEl.dataset.abRest === 'true',
        };
      }
      const regel = this.preisRegel;
      const alsPreis = (cent, muster) => String(muster).replace(/\d[\d.,]*/, (cent / 100).toFixed(2).replace('.', ','));
      if (regel) {
        const cent = Math.max(Number(zeigen.cent) + regel.pflicht, regel.mindest);
        zeigen = { ...zeigen, price: alsPreis(cent, zeigen.price) };
      }
      // Das "ab" des Artikels bleibt vor dem Preis stehen - mit Regel nur, wenn es nicht allein an den Varianten hing.
      const abEl = preisfeld.querySelector('.dp-preis__ab');
      const ab = abEl && (!regel || regel.abRest) ? abEl : null;
      /*
       * Der leere Platz fuer den Mindestpreis gehoert allein den
       * 3D-Kalkulationsartikeln: dp-ab-preis.js schreibt den Mindestbetrag der
       * Angebots-App als Wert ans Wurzelelement, und das Stilblatt setzt ihn in
       * diesen Platz - dabei tritt der eigentliche Betrag daneben ab.
       *
       * Weil die Marke am Wurzelelement haengt, gilt sie fuer die ganze Seite.
       * Ein Platz, den es im Aufbau gar nicht gab, darf hier deshalb nicht
       * entstehen: Beim Shirt kam so nach jedem Klick auf eine Farbe der
       * Mindestbetrag des 3D-Drucks heraus - "ab €5,00" statt des Shirtpreises.
       */
      const mindest = preisfeld.querySelector('.dp-preis__mindest');
      // Streichpreis nur zeigen, wenn die Variante tatsaechlich reduziert ist.
      preisfeld.innerHTML = zeigen.compareAt
        ? (ab ? ab.outerHTML : '') +
          '<s class="dp-product__price-old">' + zeigen.compareAt + '</s>' +
          '<span class="dp-product__price-new">' + zeigen.price + '</span>'
        : ab
          ? mindest
            /*
             * Nur beim 3D-Druck: Dort tritt der Betrag hinter den
             * Mindestpreis zurueck, und das Stilblatt blendet ihn dafuer aus
             * (.dp-3d-mindestpreis-da .dp-preis__betrag). Diese Regel haengt
             * am Wurzelelement und gilt fuer die ganze Seite - eine Huelle
             * dieses Namens ohne Mindestpreis daneben waere also einfach
             * unsichtbar. Genau so verschwand der Preis im Shirt-Feld.
             */
            ? '<span class="dp-preis__zeile">' + ab.outerHTML + '<span class="dp-preis__betrag">' + zeigen.price + '</span>' + mindest.outerHTML + '</span>'
            : '<span class="dp-preis__zeile">' + ab.outerHTML + zeigen.price + '</span>'
          : '<span class="dp-preis__zeile">' + zeigen.price + '</span>';
    }

    /*
     * Der Zustand steht am Feld - daran haengt, ob der Preis oder das Zeichen
     * fuer "nicht vorraetig" zu sehen ist.
     */
    this.dataset.dpVerfuegbar = treffer.available ? 'ja' : 'nein';

    const knopf = this.knopf;
    if (knopf) {
      knopf.disabled = !treffer.available;
      // Bewusst nur das Label, nicht der Inhalt: der Knopf traegt das
      // Wagensymbol, ein Setzen von textContent wuerde es entfernen.
      if (this.addLabel) {
        knopf.setAttribute('aria-label', treffer.available ? this.addLabel : this.soldOutLabel);
      }
    }
  }

  /**
   * Die Variante, mit der die Produktseite aufgehen soll.
   *
   * Nur wenn der Kunde im Feld eine Farbe gewaehlt hat - sonst bleibt es bei
   * der offenen Farbwahl der Produktseite (keine Vorauswahl). Unter den
   * passenden Varianten zaehlt zuerst, was zu haben ist, dann der kleinste
   * Betrag: derselbe, den das Feld gerade als Preis zeigt. Passt keine
   * Variante zu allen gewaehlten Werten, genuegt die Farbe allein.
   *
   * @param {{ id: number, options: string[], available: boolean, cent: number }[]} passend
   */
  varianteFuerSeite(passend) {
    /*
     * Seit jede Option offen startet: eine Variante gibt es, sobald
     * irgendetwas gewaehlt ist - die Farbe oder eine Groesse. Was der Kunde
     * davon gewaehlt hat, reist als ?dp-wahl mit (aktualisiereVerweise), die
     * uebrigen Optionen stehen auf der Produktseite offen.
     */
    const radio = this.querySelector('.dp-product__option--farben input[type="radio"]:checked');
    const gewaehlt = this.selectedValues();
    if (!gewaehlt.some((wert, i) => wert !== undefined && this.istWahl(i))) return null;

    let kandidaten = passend;
    if (!kandidaten.length && radio instanceof HTMLInputElement) {
      const stelle = Number(radio.dataset.optionIndex);
      kandidaten = this.variants.filter((variante) => variante.options[stelle] === radio.value);
    }
    if (!kandidaten.length) return null;

    const vorraetig = kandidaten.filter((variante) => variante.available);
    const auswahl = vorraetig.length ? vorraetig : kandidaten;
    return auswahl.reduce((klein, v) => (Number(v.cent) < Number(klein.cent) ? v : klein), auswahl[0]);
  }

  /**
   * Die Verweise des Feldes auf die Produktseite nehmen die gewaehlte Farbe
   * mit (?variant=...): Wer im Feld eine Farbe waehlt und dann auf ein Bild,
   * den Namen oder den Stift tippt, landet auf der Produktseite mit genau
   * dieser Farbe - Auswahl, Bilder und Preis stehen dort schon darauf.
   *
   * Die Ausgangsadresse wird am Verweis gemerkt; ohne Farbe gilt sie wieder
   * unveraendert. Weitere Angaben der Adresse (etwa aus der Suche) bleiben
   * stehen.
   *
   * @param {{ id: number } | null} variante
   */
  aktualisiereVerweise(variante, gewaehlt = []) {
    // Die Positionen (ab 1) der Optionen, die der Kunde hier gewaehlt hat.
    const wahl = [];
    gewaehlt.forEach((wert, i) => {
      if (wert !== undefined && this.istWahl(i)) wahl.push(i + 1);
    });
    // Gemeinsamer Baustein mit den Farbpunkten der Kachel (dp-variantenverweis.js).
    setzeVariantenVerweise(
      this.querySelectorAll('a.dp-product__panel-link, .dp-product__panel-media > a.dp-product__panel-slot, a.dp-product__anpassen'),
      variante ? variante.id : null,
      wahl
    );
  }

  /**
   * Ist die Option an dieser Stelle eine echte Wahl des Kunden - Farbpunkte
   * oder eine Liste mit mehreren Werten? Eine Liste mit nur einem Wert steht
   * von selbst fest.
   *
   * @param {number} stelle - ab 0
   */
  istWahl(stelle) {
    if (this.querySelector('.dp-product__option--farben input[data-option-index="' + stelle + '"]')) return true;
    return !!this.querySelector('select[data-option-index="' + stelle + '"][data-dp-pflichtwahl]');
  }

  markiereNichtVerfuegbar() {
    this.dataset.dpVerfuegbar = 'nein';
    const knopf = this.knopf;
    if (!knopf) return;
    knopf.classList.remove('dp-kaufknopf--ohne-farbe');
    knopf.disabled = true;
    knopf.setAttribute('aria-label', this.soldOutLabel);
  }
}

if (!customElements.get('dp-quick-buy')) {
  customElements.define('dp-quick-buy', DpQuickBuy);
}
